import { useState, useEffect } from 'react';
import { 
  Bot, 
  Server, 
  Globe, 
  Shield,
  Save,
  Loader2,
  TestTube,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { McpInfo } from '@/types';

interface SettingsState {
  defaultModel: string;
  logLevel: string;
  telegramConfigured: boolean;
  discordConfigured: boolean;
  slackConfigured: boolean;
  openrouterConfigured: boolean;
  mcpInfo: McpInfo | null;
}

export function Settings() {
  const [settings, setSettings] = useState<SettingsState>({
    defaultModel: 'openrouter:anthropic/claude-3.5-sonnet',
    logLevel: 'info',
    telegramConfigured: false,
    discordConfigured: false,
    slackConfigured: false,
    openrouterConfigured: false,
    mcpInfo: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'platforms' | 'mcp' | 'advanced'>('general');
  const [testMcpTool, setTestMcpTool] = useState('');
  const [mcpTestResult, setMcpTestResult] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
    loadMcpInfo();
  }, []);

  const loadSettings = async () => {
    try {
      const health = await apiClient.health();
      // In a real app, these would come from an API endpoint
      // For now, we'll use defaults
      setSettings(prev => ({
        ...prev,
        defaultModel: import.meta.env.VITE_DEFAULT_MODEL || 'openrouter:anthropic/claude-3.5-sonnet',
      }));
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMcpInfo = async () => {
    try {
      const info = await apiClient.mcpInfo();
      setSettings(prev => ({ ...prev, mcpInfo: info }));
    } catch (err) {
      console.error('Failed to load MCP info:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // In a real app, this would POST to an API
      // For now, just show success
      await new Promise(r => setTimeout(r, 500));
      setSuccess('Settings saved successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testMcp = async () => {
    if (!testMcpTool.trim()) return;
    setMcpTestResult('Testing...');
    try {
      const result = await apiClient.mcpCall(testMcpTool, {});
      setMcpTestResult(JSON.stringify(result, null, 2));
    } catch (err) {
      setMcpTestResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Bot },
    { id: 'platforms', label: 'Platforms', icon: Globe },
    { id: 'mcp', label: 'MCP', icon: Server },
    { id: 'advanced', label: 'Advanced', icon: Shield },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Configure Anika Agent</p>
        </div>
        <Button onClick={handleSave} loading={saving}>
          <Save className="h-4 w-4 mr-1" />
          Save Changes
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700 overflow-hidden">
        <div className="border-b border-gray-200 dark:border-dark-700">
          <nav className="flex -mb-px" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">General Settings</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default Model
                </label>
                <select
                  value={settings.defaultModel}
                  onChange={(e) => setSettings(prev => ({ ...prev, defaultModel: e.target.value }))}
                  className="w-full max-w-md px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <option value="openrouter:anthropic/claude-3.5-sonnet">Claude 3.5 Sonnet (OpenRouter)</option>
                  <option value="openrouter:anthropic/claude-3-opus">Claude 3 Opus (OpenRouter)</option>
                  <option value="openrouter:openai/gpt-4o">GPT-4o (OpenRouter)</option>
                  <option value="openrouter:google/gemini-pro-1.5">Gemini Pro 1.5 (OpenRouter)</option>
                  <option value="@cf/meta/llama-3.1-8b-instruct">Llama 3.1 8B (Workers AI)</option>
                  <option value="@cf/mistral/mistral-7b-instruct-v0.2">Mistral 7B (Workers AI)</option>
                </select>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  The default model used for new conversations. Can be overridden per-session.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Log Level
                </label>
                <select
                  value={settings.logLevel}
                  onChange={(e) => setSettings(prev => ({ ...prev, logLevel: e.target.value }))}
                  className="w-full max-w-md px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                >
                  <option value="debug">Debug</option>
                  <option value="info">Info</option>
                  <option value="warn">Warn</option>
                  <option value="error">Error</option>
                </select>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-dark-800/50 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Environment Variables</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  These are configured as Worker secrets in the Cloudflare Dashboard.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center justify-between p-2 bg-white dark:bg-dark-800 rounded">
                    <span>OPENROUTER_API_KEY</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${settings.openrouterConfigured ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {settings.openrouterConfigured ? 'Configured' : 'Not Set'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white dark:bg-dark-800 rounded">
                    <span>TELEGRAM_BOT_TOKEN</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${settings.telegramConfigured ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {settings.telegramConfigured ? 'Configured' : 'Not Set'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white dark:bg-dark-800 rounded">
                    <span>DISCORD_BOT_TOKEN</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${settings.discordConfigured ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {settings.discordConfigured ? 'Configured' : 'Not Set'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white dark:bg-dark-800 rounded">
                    <span>SLACK_BOT_TOKEN</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${settings.slackConfigured ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {settings.slackConfigured ? 'Configured' : 'Not Set'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Platforms Tab */}
          {activeTab === 'platforms' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Platform Configuration</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Telegram */}
                <PlatformCard
                  name="Telegram"
                  icon={<Bot className="h-6 w-6" />}
                  configured={settings.telegramConfigured}
                  fields={[
                    { label: 'Bot Token', env: 'TELEGRAM_BOT_TOKEN', type: 'password' },
                    { label: 'Webhook Secret', env: 'TELEGRAM_WEBHOOK_SECRET', type: 'password' },
                  ]}
                  webhookUrl="https://anika-agent.tnvmac.workers.dev/webhook/telegram"
                  setupUrl="https://core.telegram.org/bots#creating-a-new-bot"
                />
                
                {/* Discord */}
                <PlatformCard
                  name="Discord"
                  icon={<Bot className="h-6 w-6" />}
                  configured={settings.discordConfigured}
                  fields={[
                    { label: 'Bot Token', env: 'DISCORD_BOT_TOKEN', type: 'password' },
                    { label: 'Public Key', env: 'DISCORD_PUBLIC_KEY', type: 'text' },
                  ]}
                  webhookUrl="https://anika-agent.tnvmac.workers.dev/webhook/discord"
                  setupUrl="https://discord.com/developers/applications"
                />
                
                {/* Slack */}
                <PlatformCard
                  name="Slack"
                  icon={<Bot className="h-6 w-6" />}
                  configured={settings.slackConfigured}
                  fields={[
                    { label: 'Bot Token', env: 'SLACK_BOT_TOKEN', type: 'password' },
                    { label: 'Signing Secret', env: 'SLACK_SIGNING_SECRET', type: 'password' },
                  ]}
                  webhookUrl="https://anika-agent.tnvmac.workers.dev/webhook/slack"
                  setupUrl="https://api.slack.com/apps"
                />
              </div>
            </div>
          )}

          {/* MCP Tab */}
          {activeTab === 'mcp' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">MCP Server</h2>
              
              <div className="bg-white dark:bg-dark-800/50 rounded-lg border border-gray-200 dark:border-dark-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900 dark:text-white">MCP Server Status</h3>
                  <TestTube className="h-5 w-5 text-primary-600" />
                </div>
                
                {settings.mcpInfo ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                      <p className="text-gray-500 dark:text-gray-400">Name</p>
                      <p className="font-mono text-gray-900 dark:text-white">{settings.mcpInfo.name}</p>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                      <p className="text-gray-500 dark:text-gray-400">Version</p>
                      <p className="font-mono text-gray-900 dark:text-white">{settings.mcpInfo.version}</p>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                      <p className="text-gray-500 dark:text-gray-400">Protocol</p>
                      <p className="font-mono text-gray-900 dark:text-white">{settings.mcpInfo.protocol}</p>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                      <p className="text-gray-500 dark:text-gray-400">Tools Available</p>
                      <p className="font-mono text-gray-900 dark:text-white">{settings.mcpInfo.tools_count}</p>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded sm:col-span-2">
                      <p className="text-gray-500 dark:text-gray-400">Transports</p>
                      <p className="font-mono text-gray-900 dark:text-white">{settings.mcpInfo.transports.join(', ')}</p>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded sm:col-span-2">
                      <p className="text-gray-500 dark:text-gray-400">Configured Servers</p>
                      <p className="font-mono text-gray-900 dark:text-white">{settings.mcpInfo.servers_count}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">MCP server info not available</p>
                )}
              </div>

              {/* Test MCP Tool */}
              <div className="bg-white dark:bg-dark-800/50 rounded-lg border border-gray-200 dark:border-dark-700 p-6">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">Test MCP Tool</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={testMcpTool}
                    onChange={(e) => setTestMcpTool(e.target.value)}
                    placeholder="Tool name (e.g., conversations_list, session_search)"
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                  />
                  <Button onClick={testMcp} loading={mcpTestResult === 'Testing...'}>
                    Test
                  </Button>
                </div>
                {mcpTestResult && (
                  <div className="mt-4 p-4 bg-gray-100 dark:bg-dark-800 rounded-lg max-h-96 overflow-auto">
                    <pre className="text-xs whitespace-pre-wrap font-mono text-gray-900 dark:text-white">{mcpTestResult}</pre>
                  </div>
                )}
              </div>

              {/* MCP Client Config */}
              <div className="bg-white dark:bg-dark-800/50 rounded-lg border border-gray-200 dark:border-dark-700 p-6">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">MCP Client Configuration</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Add this to your MCP client (Claude Desktop, Cursor, etc.):
                </p>
                <div className="bg-gray-100 dark:bg-dark-800 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  {`{
  "mcpServers": {
    "anika": {
      "url": "https://anika-agent.tnvmac.workers.dev/mcp",
      "transport": "streamable-http"
    }
  }
}`}
                </div>
              </div>
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Advanced Settings</h2>
              
              <div className="bg-white dark:bg-dark-800/50 rounded-lg border border-gray-200 dark:border-dark-700 p-6">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">Database</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">D1 Database</p>
                    <p className="font-mono text-gray-900 dark:text-white">anika-sessions</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">Database ID</p>
                    <p className="font-mono text-gray-900 dark:text-white">06444ea2-9198-4fd3-af8b-e0e02f256b8f</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">KV Namespace</p>
                    <p className="font-mono text-gray-900 dark:text-white">ANIKA_HOME</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">KV ID</p>
                    <p className="font-mono text-gray-900 dark:text-white">24dc536fab0540e0a2686bf62b83dc64</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-dark-800/50 rounded-lg border border-gray-200 dark:border-dark-700 p-6">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">Queues</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">anika-cron</p>
                    <p className="font-mono text-gray-900 dark:text-white">Cron jobs</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">anika-mcp</p>
                    <p className="font-mono text-gray-900 dark:text-white">MCP tool calls</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">anika-delegation</p>
                    <p className="font-mono text-gray-900 dark:text-white">Subagent delegation</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">anika-background</p>
                    <p className="font-mono text-gray-900 dark:text-white">Background tasks</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-dark-800/50 rounded-lg border border-gray-200 dark:border-dark-700 p-6">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">Durable Objects</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">GatewayDurableObject</p>
                    <p className="font-mono text-gray-900 dark:text-white">WebSocket gateway</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">AgentDurableObject</p>
                    <p className="font-mono text-gray-900 dark:text-white">Conversation loop</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">SessionDurableObject</p>
                    <p className="font-mono text-gray-900 dark:text-white">Session state</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded">
                    <p className="text-gray-500 dark:text-gray-400">McpServerDurableObject</p>
                    <p className="font-mono text-gray-900 dark:text-white">MCP server</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlatformCard({ 
  name, 
  icon, 
  configured, 
  fields, 
  webhookUrl, 
  setupUrl 
}: { 
  name: string; 
  icon: React.ReactNode; 
  configured: boolean; 
  fields: { label: string; env: string; type: 'text' | 'password' }[];
  webhookUrl: string;
  setupUrl: string;
}) {
  return (
    <div className="bg-white dark:bg-dark-800/50 rounded-xl border border-gray-200 dark:border-dark-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            {icon}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {configured ? 'Configured' : 'Not configured'}
            </p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          configured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
        }`}>
          {configured ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="space-y-3 mb-4">
        {fields.map((field) => (
          <div key={field.label} className="p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {field.label} ({field.env})
            </label>
            <input
              type={field.type}
              placeholder={configured ? '••••••••' : 'Not set'}
              disabled={true}
              className="w-full bg-transparent text-gray-900 dark:text-white"
            />
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Webhook URL
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={webhookUrl}
              readOnly
              className="flex-1 bg-transparent text-gray-900 dark:text-white font-mono text-sm"
            />
            <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(webhookUrl)}>
              Copy
            </Button>
          </div>
        </div>
        <a
          href={setupUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary-600 hover:underline dark:text-primary-400"
        >
          Setup Guide →
        </a>
      </div>
    </div>
  );
}